import type { ComponentType } from "react";
import Select, {
	type GroupBase,
	type GroupProps,
	type Props,
	type StylesConfig,
} from "react-select";
import Divider from "../../components/general/Divider";
import * as s from "./Dropdown.module.scss";

const Dropdown = <
	DropdownOption,
	IsMulti extends boolean = false,
	Group extends GroupBase<DropdownOption> = GroupBase<DropdownOption>,
>(
	props: Props<DropdownOption, IsMulti, Group>,
) => {
	const Group: ComponentType<GroupProps<DropdownOption, IsMulti, Group>> = (
		data,
	) => {
		return (
			<div className={s.group}>
				<span className={s.groupLabel}>{data.label}</span>
				<ul>{data.children}</ul>
				<div className={s.divider}>
					<Divider variant="horizontal" />
				</div>
			</div>
		);
	};

	const colorStyles: StylesConfig<DropdownOption, IsMulti, Group> = {
		container: (styles, state) => ({
			...styles,
			borderRadius: state.selectProps.menuIsOpen
				? " 0.375rem 0.375rem 0 0"
				: "0.375rem",
			boxShadow: state.selectProps.menuIsOpen
				? "0px 5px 9px rgba(39, 39, 39, 0.1)"
				: "none",
		}),
		control: (styles, state) => ({
			...styles,
			margin: "0 0",
			border: "1px solid none",
			borderColor: s.stroke,
			borderRadius: state.selectProps.menuIsOpen
				? " 0.375rem 0.375rem 0 0"
				: "0.375rem",
			boxShadow: "none",
			"&:hover": {
				borderColor: s.stroke,
			},
			padding: "0.5rem 1rem 0.5rem 1rem",
			"@media (min-width: 768px)": {
				padding: "1rem",
			},
		}),
		singleValue: (styles) => {
			return {
				...styles,
				fontWeight: "bold",
			};
		},

		placeholder: (styles) => {
			return {
				...styles,
				padding: "0.5rem",
				margin: 0,
			};
		},
		valueContainer: (styles) => {
			return {
				...styles,
				padding: 0,
			};
		},
		menu: (styles) => ({
			...styles,
			boxSizing: "border-box",
			margin: "0 0",

			border: `1px solid ${s.grey}`,
			borderTop: "none",
			borderColor: s.stroke,
			borderRadius: "0 0 0.375rem 0.375rem",
			boxShadow: "0px 5px 9px rgba(39, 39, 39, 0.1)",
		}),
		menuList: (styles) => ({
			...styles,
			padding: "2rem 0",
		}),
		dropdownIndicator: (styles, state) => {
			return {
				...styles,
				color: s.grey,
				padding: 0,
				transition: "transform .2s ease",
				transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "none",
			};
		},
		option: (styles, { isFocused, isSelected }) => {
			return {
				...styles,
				padding: "0 1rem 0 1rem",
				color: s.grey,
				backgroundColor:
					isFocused || isSelected ? s.optionSelectedColor : "none",
				boxShadow: "none",
			};
		},
	};

	return (
		<Select
			{...props}
			styles={colorStyles}
			isSearchable={false}
			components={{
				...props.components,
				IndicatorSeparator: () => null,
				Group: Group,
			}}
			options={props.options}
			className={s.select}
			onChange={props.onChange}
		/>
	);
};

export default Dropdown;
